import { useCallback, useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { useLogoutMutation } from "../../api/auth";
import { useToast } from "../../components/ui";
import { clearStore, useStore } from "../../store";
import { getCookie } from "@/utils/cookies";
import { queryClient } from "../../router/queryClient";

export const useLogout = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const resetAuthStore = useStore.use.resetAuthStore();
  const setEntityFeatureFlagShowHide =
    useStore.use.setEntityFeatureFlagShowHide();
  const refresh_token_var =
    localStorage.getItem("refresh_token") ?? getCookie("refresh_token");
  const [isLogoutLoading, setIsLogoutLoading] = useState(false);
  const { mutateAsync } = useLogoutMutation({
    onError: (error) => {
      toast({
        variant: "destructive",
        title: error?.message ?? "Logout failed.",
      });
    },
  });

  const handleLogout = useCallback(async () => {
    setIsLogoutLoading(true);

    // Start server-side logout, but do not await it before leaving the protected
    // route.
    const logoutRequest = refresh_token_var
      ? mutateAsync(refresh_token_var).catch(() => undefined)
      : Promise.resolve();

    try {
      // Keep the current auth state until the login route has mounted. This
      // prevents the app shell from rendering its restricted fallback while a
      // lazy route transition is in progress.
      await navigate({ to: "/auth/login", replace: true });
    } catch {
      // Preserve a reliable escape path if the client router cannot navigate.
      resetAuthStore();
      clearStore();
      setEntityFeatureFlagShowHide([]);
      queryClient.clear();
      window.location.replace(
        `${window.location.origin}/auth/login?logout=true`,
      );
      await logoutRequest;
      return;
    }

    resetAuthStore(); // Zustand in-memory state
    clearStore(); // Zustand persisted store/localStorage
    setEntityFeatureFlagShowHide([]); // Any UI flags
    queryClient.clear();

    // The login route is visible; this only lets callers/tests observe
    // completion of the best-effort server logout request.
    await logoutRequest;
  }, [
    refresh_token_var,
    mutateAsync,
    navigate,
    resetAuthStore,
    setEntityFeatureFlagShowHide,
  ]);
  return {
    handleLogout,
    isLogoutLoading,
  };
};
